/**
 * Buchungsstrecke C3 (Mockup Station 6, docs/KONZEPT-VERKAUFSPSYCHOLOGIE.md § 4 Punkt 6): die
 * Vorfreude-Karte unter „Es ist fix!“ auf der Angebotsseite nach der Zusage.
 *
 * Holt ihre Sicht selbst (`GET /api/public/offer/:token/vorfreude`, sql/597) und rechnet nichts:
 * die Zahl der Tage bis zum Fest, die Gästezahl-Frist und der Ort kommen fertig vom Server. Ohne
 * Antwort (Fehler, Fest vorbei, noch keine Zusage) zeigt sie nichts — die Bestätigung darüber
 * bleibt, wie sie ist.
 *
 * Nächste Schritte nur, wenn es sie gibt: eine offene Zahlung/Kostenübernahme (sagt die Seite,
 * `offen`), die Gästezahl-Frist (nur wenn der Betrieb eine hat), dann der Abend selbst. Kein
 * Countdown, kein Verkaufsversuch (Konzept § 5).
 */
import { useEffect, useState } from "react";
import { ButtonLink, Card, Icon } from "./components";

export interface VorfreudeSicht {
  datum: string;
  tageBis: number;
  uhrzeit: string | null;
  anlass: string | null;
  gaeste: number | null;
  gaestezahlFrist: string | null;
  ort: string | null;
  betrieb: string | null;
  ansprechpartner: { name: string; telefon: string | null } | null;
  /** Weitere Ansprechpartner — fehlt bei älteren Servern. */
  weitere?: { name: string; telefon: string | null }[];
  /** Einstellungen → Team: „gleichberechtigt“ = alle gleich mit Nummer, sonst Hauptperson + Vertretung. */
  darstellung?: "haupt_vertretung" | "gleichberechtigt";
  telefon: string | null;
}

const T = {
  de: {
    fest: "Ihr Fest",
    in: (n: number) => (n === 0 ? "heute" : n === 1 ? "morgen" : `in ${n} Tagen`),
    weiter: "So geht es weiter",
    zahlung: "Zahlung abschließen",
    zahlungText: "Erst damit ist Ihr Termin fest. Den Weg dazu finden Sie direkt hier auf der Seite.",
    ku: "Kostenübernahme unterschreiben",
    kuText: "Den Link zur digitalen Unterschrift erhalten Sie per E-Mail.",
    frist: (d: string) => `Bis ${d}`,
    fristText: "endgültige Gästezahl und Allergien mitteilen – antworten Sie einfach auf unsere E-Mail.",
    uhr: (u: string) => `, ${u} Uhr`,
    gaeste: (n: number) => `${n} Gäste`,
    kalender: "In meinen Kalender",
    ansprechpartner: "Ihr Ansprechpartner",
    ansprechpartnerMehrere: "Ihre Ansprechpartner",
    vertretung: "Vertretung",
    telefon: "Telefon",
  },
  en: {
    fest: "Your celebration",
    in: (n: number) => (n === 0 ? "today" : n === 1 ? "tomorrow" : `in ${n} days`),
    weiter: "What happens next",
    zahlung: "Complete your payment",
    zahlungText: "This is what secures your date. You will find the way to pay right here on this page.",
    ku: "Sign the cost acceptance",
    kuText: "You will receive the link for the digital signature by email.",
    frist: (d: string) => `By ${d}`,
    fristText: "let us know the final number of guests and any allergies – simply reply to our email.",
    uhr: (u: string) => ` at ${u}`,
    gaeste: (n: number) => `${n} guests`,
    kalender: "Add to my calendar",
    ansprechpartner: "Your contact",
    ansprechpartnerMehrere: "Your contacts",
    vertretung: "Deputy",
    telefon: "Phone",
  },
} as const;

const datumLang = (iso: string, lang: "de" | "en") =>
  new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }).format(new Date(`${iso}T00:00:00Z`));

/** Nur eine Antwort in genau dieser Form wird gezeigt — alles andere heißt „nichts zu zeigen“. */
function istSicht(x: unknown): x is VorfreudeSicht {
  const s = x as Partial<VorfreudeSicht> | null;
  return !!s && typeof s.datum === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.datum) && typeof s.tageBis === "number" && s.tageBis >= 0;
}

export function VorfreudeKarte({ token, lang, offen }: { token: string | undefined; lang: "de" | "en"; offen: "fix" | "zahlung" | "kostenuebernahme" }) {
  const [sicht, setSicht] = useState<VorfreudeSicht | null>(null);
  useEffect(() => {
    if (!token) return;
    let aktiv = true;
    fetch(`/api/public/offer/${encodeURIComponent(token)}/vorfreude`)
      .then(async (r) => (r.ok ? ((await r.json()) as { data?: unknown }).data : null))
      .then((d) => {
        if (aktiv) setSicht(istSicht(d) ? d : null);
      })
      .catch(() => undefined);
    return () => {
      aktiv = false;
    };
  }, [token]);
  if (!token || !sicht) return null;

  const t = T[lang];
  const schritte: Array<{ titel: string; text: string }> = [];
  if (offen === "zahlung") schritte.push({ titel: t.zahlung, text: t.zahlungText });
  if (offen === "kostenuebernahme") schritte.push({ titel: t.ku, text: t.kuText });
  if (sicht.gaestezahlFrist) schritte.push({ titel: t.frist(datumLang(sicht.gaestezahlFrist, lang)), text: t.fristText });
  schritte.push({
    titel: `${datumLang(sicht.datum, lang)}${sicht.uhrzeit ? t.uhr(sicht.uhrzeit) : ""}`,
    text: [sicht.anlass ?? t.fest, sicht.gaeste ? t.gaeste(sicht.gaeste) : null, sicht.ort].filter(Boolean).join(" · "),
  });
  const ap = sicht.ansprechpartner;
  const tel = ap?.telefon ?? sicht.telefon;
  const weitere = ap ? (sicht.weitere ?? []) : [];
  // Antoine 07.10.2026: wie die Angebotsseite — gleichberechtigt alle mit Nummer, sonst Vertretung ohne Direktnummer.
  const gleich = weitere.length > 0 && sicht.darstellung === "gleichberechtigt";
  const telLink = (nr: string) => (
    <a href={`tel:${nr.replace(/[^+0-9]/g, "")}`} className="underline underline-offset-2">
      {nr}
    </a>
  );

  return (
    <Card className="mx-auto mt-8 max-w-md p-6 text-left" data-testid="vorfreude-karte">
      <p className="font-body-md text-body-md font-semibold text-on-surface dark:text-inverse-on-surface" data-testid="vorfreude-tage">
        {sicht.anlass ?? t.fest} · {t.in(sicht.tageBis)}
      </p>
      <p className="mt-4 font-label-sm text-label-sm uppercase tracking-[0.06em] text-on-surface-variant dark:text-secondary-fixed-dim">{t.weiter}</p>
      <ol className="mt-3 space-y-3">
        {schritte.map((s, i) => (
          <li key={s.titel} className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-container-high font-label-sm text-label-sm text-on-surface-variant dark:bg-inverse-surface dark:text-secondary-fixed-dim">{i + 1}</span>
            <span className="min-w-0">
              <span className="block font-body-md text-body-md font-semibold text-on-surface dark:text-inverse-on-surface">{s.titel}</span>
              <span className="block font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{s.text}</span>
            </span>
          </li>
        ))}
      </ol>
      <ButtonLink variant="secondary" href={`/api/public/offer/${encodeURIComponent(token)}/kalender.ics`} download className="mt-5 w-full" data-testid="vorfreude-kalender">
        <Icon name="calendar_add_on" className="text-[18px]" /> {t.kalender}
      </ButtonLink>
      {ap && gleich ? (
        <div className="mt-4 flex gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
          <Icon name="call" className="text-[18px] text-secondary dark:text-secondary-fixed-dim" />
          <div>
            <p>{t.ansprechpartnerMehrere}:</p>
            {[ap, ...weitere].map((p, i) => (
              <p key={`${p.name}-${i}`}>
                {p.name}
                {p.telefon ? <>{" · "}{telLink(p.telefon)}</> : null}
              </p>
            ))}
          </div>
        </div>
      ) : (ap || tel) && (
        <p className="mt-4 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
          <Icon name="call" className="text-[18px] text-secondary dark:text-secondary-fixed-dim" />
          <span>
            {ap ? `${t.ansprechpartner}: ${ap.name}` : t.telefon}
            {tel ? <>{" · "}{telLink(tel)}</> : null}
            {weitere.map((p, i) => (
              <span key={`${p.name}-${i}`} className="block">
                {t.vertretung}: {p.name}
              </span>
            ))}
          </span>
        </p>
      )}
    </Card>
  );
}
